import { createApi } from '@reduxjs/toolkit/query/react';

import { getData, postData } from '../../services/API';
import {
  divisionsUrl,
  facilitiesUrl,
  unitsUrl,
  unitsConfigUrl
} from '../../services/API/endpoints';

import { compareObjByStringKey } from '../../utils/helpers';
import { processError, processResponse } from './utils/transformations';

import { AuthUserProps, Facility, Unit } from '../../config/interfaces';

interface IChangeUnitLocTypeAndFlagPayload {
  facilityId: string;
  unitId: string;
  locType: any;
  active: string | undefined;
}

interface IGetActiveUnitsPayload {
  facilityId: string;
  isUnfiltered: boolean;
  active: boolean;
}

/**
 * RTK Query API slice for config features.
 *
 * @remarks
 * Provides endpoints for:
 * - Facility unit configuration (`getUnitsConfig`)
 * - Other config-related endpoints as defined
 *
 * @example
 * import { useGetUnitsConfigQuery } from 'src/redux/apis/Config.api';
 * const { data } = useGetUnitsConfigQuery();
 */
export const configApi = createApi({
  reducerPath: 'configApi',
  baseQuery: (operation: any) => operation,
  tagTypes: ['facilities', 'divisions', 'units-config'],
  invalidationBehavior: 'immediately',
  endpoints: builder => ({
    getFacilities: builder.query({
      query: () => getData(facilitiesUrl, {}),
      transformResponse: (
        response: any,
        _meta: any,
        payload: AuthUserProps
      ) => {
        const sortedFacilities = response.sort(
          compareObjByStringKey('facility')
        );

        const { access: { accessLevel, accessLevelIds = [] } = {} } = payload;

        let filteredUserFacilities = [];
        switch (accessLevel) {
          case 'master':
            filteredUserFacilities = sortedFacilities;
            break;
          case 'division':
            filteredUserFacilities = sortedFacilities.filter(
              (fac: Facility) => {
                return accessLevelIds.some(id => fac.division === id);
              }
            );
            break;
          case 'facility':
            filteredUserFacilities = sortedFacilities.filter(
              (fac: Facility) => {
                return accessLevelIds.some(id => fac.facilityId === id);
              }
            );
            break;
          default:
            filteredUserFacilities = [];
            break;
        }

        const formattedFilteredUserAccessibleFacilities =
          filteredUserFacilities.length > 0 &&
          filteredUserFacilities.map((fac: Facility) => {
            return {
              id: fac.id,
              facilityId: fac.facilityId,
              value: fac.facility,
              division: fac.division,
              isCSCEnabled: fac.isCsrnEnabled ?? false,
              isCNEdEnabled: fac.isCnedEnabled ?? false,
              isNLRValidationEnabled: fac.isValidationEnabled ?? false,
              isAIDETValidationEnabled: fac.isAIDETValidationEnabled ?? false,
              isSafetyValidationEnabled: fac.isSafetyValidationEnabled ?? false
            };
          });

        return {
          facilities: sortedFacilities,
          userAccessibleFacilities: formattedFilteredUserAccessibleFacilities
        };
      },
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        { type: 'facilities', id: payload.hcaid }
      ]
    }),
    getDivisions: builder.query({
      query: () => getData(divisionsUrl, {}),
      transformResponse: response => {
        const formattedData =
          response?.map((item: any) => {
            return { id: item, value: item };
          }) || [];
        return formattedData;
      },
      transformErrorResponse: processError,
      providesTags: () => [{ type: 'divisions' }]
    }),
    getUnits: builder.query<Unit[], IGetActiveUnitsPayload>({
      query: payload =>
        getData(
          `${unitsUrl}?facilityId=${payload.facilityId}&isUnfiltered=${payload.isUnfiltered}`,
          {}
        ),
      transformResponse: (
        response: Unit[],
        _meta,
        payload: IGetActiveUnitsPayload
      ) =>
        response.filter((unit: Unit) =>
          payload.active ? true : unit.active !== 'N'
        ),
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        { type: 'units-config', id: JSON.stringify(payload) }
      ]
    }),
    getUnitsConfig: builder.query({
      query: (facilityId: string) =>
        getData(`${unitsConfigUrl}?facilityId=${facilityId}`, {}),
      transformResponse: response => {
        const cleanData: Unit[] = Object.values(response);
        return cleanData;
      },
      transformErrorResponse: processError,
      providesTags: (_result, _error, facilityId) => [
        { type: 'units-config', id: facilityId }
      ]
    }),
    postUnitLocTypeAndFlag: builder.mutation({
      query: (payload: IChangeUnitLocTypeAndFlagPayload) =>
        postData(unitsConfigUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, payload) => [
        { type: 'units-config', id: payload.facilityId }
      ]
    })
  })
});

export const {
  useGetFacilitiesQuery,
  useLazyGetFacilitiesQuery,
  useGetDivisionsQuery,
  useLazyGetDivisionsQuery,
  useGetUnitsQuery,
  useLazyGetUnitsQuery,
  useGetUnitsConfigQuery,
  useLazyGetUnitsConfigQuery,
  usePostUnitLocTypeAndFlagMutation
} = configApi;
